/**
 * 复制到剪贴板（带「已复制」反馈）。
 *
 * 安全约定：**助记词禁止走这个 hook 持久化复制** —— `MnemonicGrid` 只用它复制
 * 加过时效提示的临时文本，且助记词默认不允许一键复制（PRD §6 关键触点）。
 */

import { useCallback, useRef, useState } from 'react';
import { toast } from '@/store/ui';

export interface UseCopyResult {
  copied: boolean;
  copy: (text: string, label?: string) => Promise<boolean>;
}

/** 优先 async Clipboard API，失败回落 `document.execCommand`。 */
async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 非安全上下文或权限被拒 → 回落
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function useCopy(resetMs = 1800): UseCopyResult {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  const copy = useCallback(
    async (text: string, label = '已复制') => {
      const ok = await writeClipboard(text);
      if (!ok) {
        toast.warn('复制失败，请手动选择文本');
        return false;
      }
      setCopied(true);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setCopied(false), resetMs);
      toast.info(label);
      return true;
    },
    [resetMs],
  );

  return { copied, copy };
}

export default useCopy;
